/**
 * Constantes del dominio de Equipo: permisos de gating, categorías de permisos
 * (réplica del SSR) y mensajes legibles por `error_code`.
 *
 * El gating de la UI usa los MISMOS permisos que exige la API y el SSR
 * (`manage_users` para usuarios, `manage_roles` para roles; ver
 * `application/api/domains/users.py`/`roles.py` y `progress/explore_equipo_ssr.md`).
 *
 * El catálogo `GET /permissions` es **plano** (`{id, description}`): no trae
 * categorías. La agrupación visual de la pantalla de permisos granulares es
 * PRESENTACIÓN del cliente y replica EXACTAMENTE las 9 categorías y sus listas de
 * `templates/manage_permissions.html` (orden incluido). Un permiso del catálogo que
 * no caiga en ninguna categoría se muestra en "Otros" para no perderlo en la UI.
 */

/** Permiso que gobierna la gestión de usuarios (igual que la API y el SSR). */
export const MANAGE_USERS_PERMISSION = "manage_users";

/** Permiso que gobierna la gestión de roles (igual que la API y el SSR). */
export const MANAGE_ROLES_PERMISSION = "manage_roles";

/** Longitud mínima de contraseña que exige el backend (`password_too_short`). */
export const PASSWORD_MIN_LENGTH = 6;

/**
 * Una categoría de permisos para la UI de permisos granulares / roles. `id` es
 * estable (para `key`); `permissions` lista los ids que pertenecen a la categoría
 * (en el orden del SSR).
 */
export interface PermissionCategory {
  id: string;
  label: string;
  permissions: string[];
}

/**
 * Las 9 categorías del SSR (`templates/manage_permissions.html`), en su orden y
 * con las MISMAS listas de permisos. Se preserva tal cual para no inventar una
 * taxonomía distinta a la que el usuario ya conoce.
 */
export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: "basic",
    label: "Permisos básicos",
    permissions: ["all"],
  },
  {
    id: "system",
    label: "Permisos del sistema",
    permissions: [
      "manage_system_settings",
      "manage_work_hours",
      "manage_attendants",
      "manage_backups",
      "manage_logs",
      "configure_auto_export",
      "manage_export_schedules",
      "configure_export_formats",
      "settings",
    ],
  },
  {
    id: "orders_basic",
    label: "Gestión de órdenes básicas",
    permissions: [
      "add_orders",
      "edit_orders",
      "delete_orders",
      "view_orders",
      "view_completed_orders",
      "view_delivered_orders",
      "view_cancelled_orders",
      "complete_orders",
      "deliver_orders",
      "cancel_orders",
      "reactivate_orders",
      "search_orders",
    ],
  },
  {
    id: "orders_granular",
    label: "Edición granular de órdenes",
    permissions: [
      "edit_order_number",
      "edit_customer_name",
      "edit_attendant",
      "edit_sale_amount",
      "edit_description",
      "edit_invoice_date",
      "edit_delivery_date",
      "edit_delivery_time",
      "edit_payment_status",
      "edit_amount_paid",
      "edit_remaining_amount",
      "edit_talonario_number",
    ],
  },
  {
    id: "states",
    label: "Gestión de estados",
    permissions: [
      "change_to_pendiente",
      "change_to_completado",
      "change_to_entregado",
      "change_to_anulado",
      "change_to_reactivado",
      "edit_completed_at",
      "edit_delivered_at",
      "edit_created_at",
      "edit_updated_at",
    ],
  },
  {
    id: "talonarios",
    label: "Gestión de talonarios",
    permissions: ["manage_talonarios", "manage_talonario_marks"],
  },
  {
    id: "visualization",
    label: "Permisos de visualización",
    permissions: ["schedule", "laser_schedule", "view_reports", "use_cotizador"],
  },
  {
    id: "pedidos_laminas",
    label: "Pedidos de láminas",
    permissions: [
      "view_pedidos_laminas",
      "add_pedidos_laminas",
      "edit_pedidos_laminas",
      "send_pedidos_laminas",
      "receive_pedidos_laminas",
      "delete_pedidos_laminas",
      "view_historial_pedidos_laminas",
      "manage_pedidos_laminas",
    ],
  },
  {
    id: "inventory",
    label: "Inventario",
    permissions: ["view_inventory", "manage_inventory"],
  },
];

/** Etiqueta de la categoría "cajón de sastre" para permisos sin categoría del SSR. */
export const OTHER_CATEGORY_LABEL = "Otros";

/**
 * Diccionario de mensajes legibles por `error_code` de las APIs de usuarios y
 * roles (`application/api/domains/users.py`/`roles.py`). Si llega un código no
 * listado, se cae al `message` del propio `ApiError` (el backend ya lo da en
 * español).
 */
export const TEAM_ERROR_MESSAGE: Record<string, string> = {
  // Usuarios
  missing_fields: "Faltan campos obligatorios.",
  password_too_short: `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres.`,
  duplicate_username: "El nombre de usuario ya existe.",
  role_not_found: "El rol indicado no existe.",
  sede_not_found: "La sede indicada no existe.",
  self_delete: "No puede eliminarse a sí mismo.",
  invalid_permissions: "Los permisos deben ser una lista válida.",
  // `last_admin` NO se mapea: el backend ya da un mensaje claro ("No se puede
  // eliminar el último administrador.") y se prefiere su texto.
  // Roles
  invalid_role_id: "El id del rol solo admite minúsculas, números y guion bajo.",
  duplicate_role_id: "Ya existe un rol con ese id.",
  name_required: "El nombre del rol es obligatorio.",
  is_system_role: "No se pueden eliminar roles del sistema.",
  // `role_in_use` NO se mapea a propósito: el backend devuelve el conteo exacto de
  // usuarios ("hay N usuario(s) usándolo"), más útil que un texto genérico. Cae al
  // `message` del `ApiError`.
  not_found: "Recurso no encontrado.",
  // Genéricos
  forbidden: "No tienes permiso para esta acción.",
  http_error: "Ocurrió un error al comunicarse con el servidor.",
};

/** Colores (Tailwind) del badge de rol, por id de rol del sistema (paridad SSR). */
export const ROLE_BADGE_CLASS: Record<string, string> = {
  admin: "bg-red-100 text-red-700",
  supervisor: "bg-amber-100 text-amber-700",
  operario: "bg-blue-100 text-blue-700",
  entregas: "bg-emerald-100 text-emerald-700",
  logistica: "bg-purple-100 text-purple-700",
  mostrador: "bg-cyan-100 text-cyan-700",
  caja: "bg-indigo-100 text-indigo-700",
};

/** Clase por defecto del badge de rol (roles personalizados / desconocidos). */
export const ROLE_BADGE_DEFAULT_CLASS = "bg-slate-100 text-slate-600";
